import { rufe } from './client';
import type { Auftragsstatus, Vertriebsform } from './auftraege';

/**
 * Der Zugang zu den Rahmenauftraegen (AUF-25 bis AUF-27).
 *
 * DIE RESTMENGE KOMMT VOM SERVER UND WIRD HIER NICHT GERECHNET. Sie ergibt
 * sich aus den Abrufauftraegen (`fach.rahmenauftrag_stand`); eine Subtraktion
 * im Browser waere die zweite Wahrheit und wiche ab dem ersten Storno ab.
 */

export type Rahmenstatus = 'aktiv' | 'ausgeschoepft' | 'abgelaufen' | 'abgeschlossen';

export const RAHMENSTATUSNAME: Readonly<Record<Rahmenstatus, string>> = {
  aktiv: 'aktiv',
  ausgeschoepft: 'ausgeschöpft',
  abgelaufen: 'abgelaufen',
  abgeschlossen: 'abgeschlossen',
};

/** AUF-27: was mit der nicht abgerufenen Menge geschieht. */
export type Restmengenregel = 'verfallen' | 'verlaengern' | 'berechnen';

export const RESTMENGENNAME: Readonly<Record<Restmengenregel, string>> = {
  verfallen: 'verfällt',
  verlaengern: 'Laufzeit verlängern',
  berechnen: 'wird berechnet',
};

export type Rahmenstand = {
  readonly abrufmenge: string;
  readonly abgerufen: string;
  readonly restmenge: string;
  readonly ausgeschoepftProzent: string;
  readonly abrufe: number;
  readonly tageBisAblauf: number;
};

export type Rahmenzeile = {
  readonly id: string;
  readonly rahmennummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly bezeichnung: string;
  readonly artikelnummer: string | null;
  readonly artikelbezeichnung: string | null;
  readonly status: Rahmenstatus;
  readonly laufzeitVon: string;
  readonly laufzeitBis: string;
  readonly stand: Rahmenstand;
};

export type Rahmenauftrag = {
  readonly id: string;
  readonly rahmennummer: string;
  readonly kunde: string;
  readonly kundenname: string;
  readonly bezeichnung: string;
  readonly artikel: string;
  readonly artikelnummer: string | null;
  readonly artikelbezeichnung: string | null;
  readonly vertriebsform: Vertriebsform;
  readonly gebundenerPreis: string;
  readonly steuerschluessel: string;
  readonly steuerProzent: string;
  readonly abrufmenge: string;
  readonly laufzeitVon: string;
  readonly laufzeitBis: string;
  readonly meldeschwelleProzent: string;
  readonly meldevorlaufTage: number;
  readonly restmengenregel: Restmengenregel | null;
  readonly verlaengerungMonate: number | null;
  readonly status: Rahmenstatus;
  readonly bemerkung: string | null;
  readonly schwelleGemeldetAm: string | null;
  readonly ablaufGemeldetAm: string | null;
  readonly bearbeiterName: string | null;
  readonly angelegtAm: string;
  readonly stand: string;
  readonly kennzahlen: Rahmenstand;
  readonly abrufe: readonly {
    readonly id: string;
    readonly auftragsnummer: string;
    readonly version: number;
    readonly belegdatum: string;
    readonly status: Auftragsstatus;
    readonly menge: string;
  }[];
};

export type Rahmenmeldung = {
  readonly rahmen: string;
  readonly rahmennummer: string;
  readonly kunde: string;
  readonly anlass: 'ausschoepfung' | 'ablauf';
  readonly restmenge: string;
  readonly ausgeschoepftProzent: string;
  readonly laufzeitBis: string;
};

/* ── Zugriffe ────────────────────────────────────────────────────────────── */

export async function liste(filter: {
  readonly kunde?: string;
  readonly status?: Rahmenstatus | '';
  readonly suche?: string;
}): Promise<readonly Rahmenzeile[]> {
  const teile = new URLSearchParams();
  if (filter.kunde !== undefined && filter.kunde !== '') teile.set('kunde', filter.kunde);
  if (filter.status !== undefined && filter.status !== '') teile.set('status', filter.status);
  if (filter.suche !== undefined && filter.suche !== '') teile.set('suche', filter.suche);
  const frage = teile.toString();
  return rufe(`/rahmenauftraege${frage === '' ? '' : `?${frage}`}`);
}

export async function akte(id: string): Promise<Rahmenauftrag> {
  return rufe(`/rahmenauftraege/${id}`);
}

export async function meldungen(stichtag?: string): Promise<readonly Rahmenmeldung[]> {
  return rufe(
    `/rahmenauftraege/meldungen${stichtag === undefined ? '' : `?stichtag=${stichtag}`}`,
  );
}

export async function anlegen(eingabe: {
  readonly kunde: string;
  readonly bezeichnung: string;
  readonly artikel: string;
  readonly vertriebsform?: Vertriebsform | null;
  /** `null` heisst "der Preis, der zum Beginn der Laufzeit gilt" (PRF-05). */
  readonly gebundenerPreis?: string | null;
  readonly abrufmenge: string;
  readonly laufzeitVon: string;
  readonly laufzeitBis: string;
  readonly meldeschwelleProzent?: string | null;
  readonly meldevorlaufTage?: number | null;
  readonly restmengenregel?: Restmengenregel | null;
  readonly verlaengerungMonate?: number | null;
  readonly bemerkung?: string | null;
}): Promise<{ readonly id: string; readonly rahmennummer: string }> {
  return rufe('/rahmenauftraege', { verfahren: 'POST', rumpf: eingabe });
}

export async function feldSichern(
  id: string,
  feld: string,
  wert: string | number | null,
  stand: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/rahmenauftraege/${id}`, { verfahren: 'PATCH', rumpf: { stand, [feld]: wert } });
}

/** AUF-25: "Abrufe erzeugen Einzelauftraege und mindern die Restmenge." */
export async function abrufen(
  id: string,
  eingabe: {
    readonly menge: number;
    readonly betreff?: string | null;
    readonly bestellreferenz?: string | null;
    readonly belegdatum: string;
    /** L5 (CRM-116): der Termin der Abrufzeile; ohne ihn bleibt er offen. */
    readonly termin?: string | null;
    /** BUC-76: bis wann die Plätze zurückgelegt werden (JJJJ-MM-TT). */
    readonly optionsfrist?: string;
  },
): Promise<{ readonly id: string; readonly auftragsnummer: string }> {
  return rufe(`/rahmenauftraege/${id}/abrufe`, { verfahren: 'POST', rumpf: eingabe });
}

/** AUF-27: bei Laufzeitende anwenden beziehungsweise vorschlagen. */
export async function restmengeAbschliessen(id: string): Promise<{
  readonly angewandt: boolean;
  readonly vorschlag: Restmengenregel | null;
  readonly restmenge: string;
}> {
  return rufe(`/rahmenauftraege/${id}/restmenge`, { verfahren: 'POST' });
}

export async function loeschen(id: string): Promise<void> {
  await rufe(`/rahmenauftraege/${id}`, { verfahren: 'DELETE' });
}
